// Qué hay a medias en la caja, para que nada recargue la página encima.
//
// El terminal se recarga solo en cuatro sitios: cuando detecta una versión nueva
// --y la busca cada quince minutos--, cuando el service worker toma el control,
// cuando el service worker se activa, y cuando el aviso de error crítico agota su
// cuenta atrás. Ninguno miraba si el cajero estaba armando una venta. Una recarga
// se lleva las líneas, el cliente y las formas de pago que todavía no se han
// confirmado: no queda ni un pendiente atascado ni un error, porque esa venta
// nunca llegó a existir. El cajero la vuelve a teclear delante del cliente, si se
// acuerda de todo.
//
// La idea es una sola: quien tiene trabajo a medias lo anota aquí, y quien quiere
// recargar pregunta antes. Si no se puede, la recarga **no se pierde**: queda en
// espera y se dispara sola en cuanto se suelta el último trabajo.
//
// **El precio de esto es un retardo sin cota, y conviene saberlo al planificar.**
// Una caja con una venta a medias puede seguir corriendo la versión anterior
// indefinidamente: la recarga espera al cajero, no al reloj. Así que
// «el terminal de tal ambiente ya está actualizado» **no es una frase
// comprobable**; la unidad no es el ambiente, es cada caja.
//
// Salió al planificar con Business Central el despliegue del cliente bloqueado
// (21/09/2026), donde una caja rezagada habría enseñado clientes que no se
// pueden facturar sin ninguna marca. Cuando el orden entre los dos lados
// importe, no se infiere: se mira el `meta.json` que la propia aplicación sirve
// con su versión, caja por caja. Y si no se puede mirar, se espera a que pase un
// cierre de caja, que es el único momento en que ninguna caja puede tener
// trabajo retenido.
//
// El retardo no es un defecto que arreglar: una recarga encima de una venta a
// medias se lleva lo tecleado sin dejar rastro, y eso es peor. Es un coste
// elegido, y está escrito aquí para que quien planifique lo cuente.
//
// **El módulo decide, pero no recarga.** Cada sitio le pasa su propia forma de
// recargar y este módulo la llama cuando toca. Así no hay ningún
// `window.location.reload()` escondido acá dentro --que en las pruebas no se
// puede ejecutar-- y la decisión, que es lo que importa, se prueba sola.
import { crearRegistro } from "../utils/registro";

const registro = crearRegistro("recarga");

/** Lo que está a medias ahora mismo: identificador → motivo legible. */
const enCurso = new Map();

/** La recarga que se quedó esperando, si la hay. */
let enEspera = null;

/** Qué hay a medias, en palabras, para poder decirlo en un aviso. */
export const trabajosEnCurso = () => [...enCurso.values()];

export const hayTrabajoEnCurso = () => enCurso.size > 0;

/** La recarga aplazada, o `null`. Se expone para poder enseñarla en pantalla. */
export const recargaEnEspera = () => (enEspera ? enEspera.motivo : null);

/**
 * Anota que hay algo a medias.
 *
 * @param {string} id      Quién lo anota. Volver a anotar el mismo `id` sustituye
 *                         el motivo en vez de duplicarlo: un formulario que se
 *                         vuelve a renderizar no cuenta dos veces.
 * @param {string} motivo  Qué es, dicho para el cajero: «una venta a medias».
 */
export const marcarTrabajo = (id, motivo) => {
  enCurso.set(id, motivo);
};

/**
 * Suelta un trabajo. Si era el último y había una recarga esperando, la dispara.
 *
 * @returns {boolean} Si esta llamada acabó recargando.
 */
export const soltarTrabajo = (id) => {
  if (!enCurso.delete(id)) return false;
  if (enCurso.size > 0 || !enEspera) return false;

  const { motivo, recargar } = enEspera;
  enEspera = null;
  registro.log(`Se terminó lo que estaba a medias; se aplica la recarga aplazada: ${motivo}`);
  recargar();
  return true;
};

/**
 * Pide recargar la página.
 *
 * Con la caja libre recarga en el acto, como hasta ahora. Con algo a medias la
 * deja en espera y avisa --una sola vez, y sin bloquear-- de que se aplicará al
 * terminar. Si ya había otra esperando se queda la primera: son todas la misma
 * cosa (volver a cargar la aplicación) y avisar dos veces sólo molesta.
 *
 * @param {string} motivo      Por qué se quiere recargar, para el registro y el aviso.
 * @param {Function} recargar  Cómo recarga quien lo pide.
 * @returns {"ahora"|"en espera"|"ya en espera"}
 */
export const pedirRecarga = (motivo, recargar) => {
  if (!hayTrabajoEnCurso()) {
    registro.log(`Se recarga la página: ${motivo}`);
    recargar();
    return "ahora";
  }

  if (enEspera) {
    registro.log(`Ya había una recarga en espera; se ignora la de ${motivo}`);
    return "ya en espera";
  }

  enEspera = { motivo, recargar };
  registro.warn(`Recarga aplazada (${motivo}): ${trabajosEnCurso().join(", ")}`);
  avisarDeLaEspera();
  return "en espera";
};

/**
 * El aviso de que la recarga queda para después.
 *
 * Va por `messageApi`, que puede no existir todavía: la primera comprobación de
 * versión corre antes de que React monte. No pasa nada, porque en ese momento
 * tampoco hay nada a medias; pero si alguna vez lo hubiera, es mejor quedarse sin
 * aviso que tirar la venta por un error al avisar.
 */
const avisarDeLaEspera = () => {
  try {
    window.messageApi?.info(
      "Hay una versión nueva del terminal. Se aplicará en cuanto termines la venta."
    );
  } catch (error) {
    registro.error("No se pudo avisar de la recarga aplazada:", error);
  }
};

/** Sólo para las pruebas: deja el módulo como recién cargado. */
export const olvidarTodo = () => {
  enCurso.clear();
  enEspera = null;
};
